import { Injectable } from '@angular/core';
import {
  AttractionDetailsRequest,
  AttractionDetailsResponse,
  AvailabilityRequest,
  Category,
  GetTopCategoryByLocationRequest,
  ProductAvailabilityRangesRequest,
  SearchRequest,
  TopCategoryItem,
} from '@grpc-clients/external/mercury/v1/attraction/attraction.pb';
import { AttractionProductOption } from '@grpc-clients/external/mercury/v1/attraction/common/attraction-product-option.pb';
import { ProductDetail } from '@grpc-clients/external/mercury/v1/attraction/common/product-detail.pb';
import { GuestType } from '@grpc-clients/external/mercury/v1/shared/guest-type.pb';
import { ProductAvailabilityRange } from '@grpc-clients/external/mercury/v1/shared/product-availability-range.pb';
import { SearchClient } from '@grpc-clients/services/attractions-service/api/search.pbsc';
import { ActivitiesForm } from '@shared/models/activities-form';
import { UppercaseGuestType } from '@shared/shared/interfaces/uppercase-guest-type';
import * as moment from 'moment';
import { Observable, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ActivitiesSearchService {
  constructor(private searchClient: SearchClient) {}

  public search(data: ActivitiesForm, languageCode: string, currencyCode: string): Observable<ProductDetail[]> {
    const startDate = moment(data.start_date_full).format('YYYY-MM-DD');
    const endDate = moment(data.end_date_full).format('YYYY-MM-DD');
    const id = Number.parseInt(data.start_location, 10);
    const category = Number.parseInt(data.object_type, 10);
    const requestData: SearchRequest.AsObject = {
      languageCode,
      currencyCode,
      startDate: category === Category.ATTRACTION_LOCATION ? startDate : null,
      endDate: category === Category.ATTRACTION_LOCATION ? endDate : null,
      tenantId: null,
      brandId: null,
      salesChannelId: null,
      productIds: [],
    };

    if (category === Category.ATTRACTION) {
      requestData.productIds = [id];
    }

    if (category === Category.ATTRACTION_LOCATION) {
      requestData.locationId = { value: id };
    }

    const request = new SearchRequest(requestData);

    return this.searchClient
      .search(request)
      .pipe(
        map((response) =>
          response.results.map((productDetailResponse) => productDetailResponse.toObject() as ProductDetail)
        )
      );
  }

  public details(languageCode: string, productId: number): Observable<AttractionDetailsResponse> {
    const requestData = {
      languageCode,
      productId,
    };
    const request = new AttractionDetailsRequest(requestData);
    return this.searchClient
      .attractionDetails(request)
      .pipe(map((response) => response.toObject() as AttractionDetailsResponse));
  }

  public availableRanges(productId: number): Observable<ProductAvailabilityRange[]> {
    // tenantId, brandId and salesChannelId is added by BE
    const requestData = { productId } as ProductAvailabilityRangesRequest.AsObject;
    const request = new ProductAvailabilityRangesRequest(requestData);

    return this.searchClient.getAvailableRanges(request).pipe(map((response) => response.availableRanges));
  }

  public categories(languageCode: string, locationId: string): Observable<TopCategoryItem[]> {
    // tenantId, brandId and salesChannelId is added by BE
    const requestData = {
      languageCode,
      locationId: Number.parseInt(locationId, 10),
    } as GetTopCategoryByLocationRequest.AsObject;
    const request = new GetTopCategoryByLocationRequest(requestData);
    return this.searchClient
      .getTopCategoryByLocation(request)
      .pipe(map((response) => response.toObject().topCategories as TopCategoryItem[]));
  }

  public checkAvailability(
    languageCode: string,
    currencyCode: string,
    productId: number,
    date: Date,
    guests: { type: UppercaseGuestType; value: number }[]
  ): Observable<AttractionProductOption[]> {
    const requestData: AvailabilityRequest.AsObject = {
      languageCode,
      currencyCode,
      travelDate: moment(date).format('YYYY-MM-DD'),
      productId,
      guests: guests.map((guest) => ({ guestType: getGuestType(guest.type), guestTypeNumber: guest.value })),
      tenantId: null,
      brandId: null,
      salesChannelId: null,
    };
    const request = new AvailabilityRequest(requestData);

    return this.searchClient
      .availability(request)
      .pipe(map((response) => response.toObject().productOptions as AttractionProductOption[]));
  }
}

function getGuestType(type: UppercaseGuestType): GuestType {
  const guestTypeMap: { [key in UppercaseGuestType]: GuestType } = {
    UNDEFINED: GuestType.Undefined,
    TRAVELER: GuestType.Traveller,
    ADULT: GuestType.Adult,
    SENIOR: GuestType.Senior,
    YOUTH: GuestType.Youth,
    CHILD: GuestType.Child,
    INFANT: GuestType.Infant,
    TRAVELLER: GuestType.Traveller,
  };

  return guestTypeMap[type];
}
